import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import Box from '../../components/Box';
import Button from '../../components/Button';
import Container from '../../components/Container';
import Section from '../../components/Section';
import backgroundImage from '../../img/hero-background.svg';
import styled from '../../styled-components';
import { Answer, Link } from '../../types/api';
import { AppState } from '../../types/store';
import {
    deleteDocument,
    fetchApplication,
    noQuestion,
    onDocumentUploaded,
    onRetry,
    policyAction,
    putPolicyAccept,
    saveAnswer,
    showQuestion,
    submitApplication
} from './actions';
import Disclaimer from './components/Disclaimer';
import Documents from './components/Documents';
import PersonalWelcome from './components/PersonalWelcome';
import QuestionSection from './components/QuestionSection';
import { isModifiable, isReadyToSubmit } from './reducer';
import { Application, Document, Question } from './types';

interface ViewApplicationProps {
    url: string;
    applicationId: string;
    application: Application;
    policyAgreeCheckbox: boolean;
    showPolicyCheckNotice: boolean;
    currentQuestion: Question | undefined;
    currentAnswer: Answer | undefined;
    retryAnswer: boolean;
    readyToSubmit: boolean;
    isModifiable: boolean;
    videoReference: string | null;
    error: boolean;
}

interface ViewApplicationDispatchProps {
    onLoad: (applicationId: string) => void;
    onClickQuestion: (question: Question) => void;
    onClickNoQuestion: () => void;
    onNewAnswer: (answer: Answer, putLink: Link) => void;
    onRetry: (answer: Answer) => void;
    onSubmitApplication: (application: Application) => void;
    onDocumentUploaded: (document: Document, link: Link) => void;
    onDeleteDocument: (document: Document) => void;
    policyAction: (agree: boolean, clicked: boolean) => void;
    putPolicyAccept: (putLink: Link) => void;
}

const NotificationLayout = Box.extend`
    background-color: ${props => props.theme.primary};
    color: ${props => props.theme.textInverted};
`;

const NotificationText = styled.div`
    flex: 1 1;
`;

const NotificationAction = styled.div`
    flex: 0 1;
`;

class ViewApplication extends React.Component<ViewApplicationProps & ViewApplicationDispatchProps, any> {
    constructor(props: ViewApplicationProps & ViewApplicationDispatchProps) {
        super(props);
        props.onLoad(props.applicationId);
        this.handleNewAnswer = this.handleNewAnswer.bind(this);
        this.handlePolicyContinueButton = this.handlePolicyContinueButton.bind(this);
    }

    private handleNewAnswer(answer: Answer) {
        if (answer._links && answer._links.self) {
            this.props.onNewAnswer(answer, answer._links.self);
        } else if (this.props.application._links) {
            this.props.onNewAnswer(answer, this.props.application._links.put_answer);
        }
    }

    private handlePolicyContinueButton() {
        this.props.policyAction(this.props.policyAgreeCheckbox, true);
        if (this.props.policyAgreeCheckbox) {
            this.props.putPolicyAccept(this.props.application._links!.put_accept_policy);
        }
    }

    public render() {
        return (
            <div style={{ overflow: 'hidden' }}>
                <Section image={backgroundImage} dark>
                    <Container>
                        <h1>Welcome to V-Sponse</h1>
                        <h3>Your video platform for interviewing questions.</h3>
                        <p>
                            V-Sponse lets you answer to the most important questions from the hiring manager for this
                            vacancy. You can answer them by simply recording a video.
                        </p>
                        <p style={{ display: 'none' }}>
                            Please also add documents like your cv or certificates. You can do this at the bottom of the
                            page.
                        </p>
                        <p>
                            We hope you enjoy using V-Sponse. If you have questions or suggestions leave us a short
                            notice:{' '}
                            <a style={{ color: 'white' }} href="mailto:contact@r-squared.ch">
                                contact@r-squared.ch
                            </a>
                        </p>
                    </Container>
                </Section>
                {this.props.error ? (
                    <Section>
                        <Container>
                            <h2>Could not load the vacancy</h2>
                            <p>Sorry, we could not load the vacancy. Please try to load the page again.</p>
                            <p>
                                In case the vacancy does not load, please contact{' '}
                                <a href="mailto:contact@r-squared.ch">contact@r-squared.ch</a>.
                            </p>
                        </Container>
                    </Section>
                ) : (
                    <PersonalWelcome
                        firstName={this.props.application.firstName}
                        lastName={this.props.application.lastName}
                        videoReference={this.props.videoReference}
                    />
                )}
                {this.props.readyToSubmit && (
                    <Section>
                        <Container>
                            <NotificationLayout>
                                <NotificationText>
                                    Congratulations! You answered all the questions. You can still change any answer you
                                    want. If you are happy with everything, you can submit it to the hiring manager.
                                    Only then they can see your videos.
                                </NotificationText>
                                <NotificationAction>
                                    <Button
                                        normal={true}
                                        onClick={() => this.props.onSubmitApplication(this.props.application)}
                                    >
                                        Submit
                                    </Button>
                                </NotificationAction>
                            </NotificationLayout>
                        </Container>
                    </Section>
                )}
                {!this.props.isModifiable && (
                    <Section>
                        <Container>
                            <NotificationLayout>
                                <NotificationText>
                                    Thank you. Your answers are submitted. We will contact you as soon as we reviewed
                                    all the videos.
                                </NotificationText>
                            </NotificationLayout>
                        </Container>
                    </Section>
                )}
                {!this.props.application.policyAccepted && !this.props.error && (
                    <Disclaimer
                        handlePolicyContinueButton={this.handlePolicyContinueButton}
                        policyAction={this.props.policyAction}
                        showPolicyCheckNotice={this.props.showPolicyCheckNotice}
                    />
                )}
                {this.props.application.policyAccepted && (
                    <React.Fragment>
                        <QuestionSection
                            application={this.props.application}
                            currentAnswer={this.props.currentAnswer}
                            currentQuestion={this.props.currentQuestion}
                            url={this.props.url}
                            retryAnswer={this.props.retryAnswer}
                            onClickQuestion={this.props.onClickQuestion}
                            onClickNoQuestion={this.props.onClickNoQuestion}
                            onRetry={this.props.onRetry}
                            handleNewAnswer={this.handleNewAnswer}
                        />
                        <Section gray style={{ display: 'none' }}>
                            <Container>
                                <h2>Attachment documents</h2>
                                {this.props.isModifiable ? (
                                    <p>Please upload any documents you want to present to the hiring manager.</p>
                                ) : (
                                    <p>You submitted following documents:</p>
                                )}
                                <Documents
                                    onDocumentUploaded={this.props.onDocumentUploaded}
                                    onDeleteDocument={this.props.onDeleteDocument}
                                    application={this.props.application}
                                    isModifiable={this.props.isModifiable}
                                />
                            </Container>
                        </Section>
                    </React.Fragment>
                )}
            </div>
        );
    }
}

export const mapStateToProps = (state: AppState, ownProps): ViewApplicationProps => {
    return {
        url: ownProps.match.url,
        applicationId: ownProps.match.params.applicationId,
        application: state.candidate.application,
        policyAgreeCheckbox: state.candidate.policyAgreeCheckbox,
        showPolicyCheckNotice: state.candidate.showPolicyCheckNotice,
        currentQuestion: state.candidate.currentQuestion,
        currentAnswer: state.candidate.currentAnswer,
        retryAnswer: state.candidate.retryAnswer,
        readyToSubmit: isReadyToSubmit(state.candidate.application),
        isModifiable: isModifiable(state.candidate.application),
        videoReference: state.candidate.application.vacancy.videoReference,
        error: state.candidate.loadError
    };
};

export const mapDispatchToProps = (dispatch: Dispatch<any>): ViewApplicationDispatchProps => {
    return {
        onLoad: (applicationId: string) => dispatch(fetchApplication(applicationId)),
        onClickQuestion: (question: Question) => dispatch(showQuestion(question)),
        onClickNoQuestion: () => dispatch(noQuestion()),
        onNewAnswer: (answer: Answer, putLink: Link) => dispatch(saveAnswer(answer, putLink)),
        onRetry: (answer: Answer) => dispatch(onRetry(answer)),
        onSubmitApplication: (application: Application) => dispatch(submitApplication(application)),
        onDocumentUploaded: (document: Document, putLink: Link) => dispatch(onDocumentUploaded(document, putLink)),
        onDeleteDocument: (document: Document) => dispatch(deleteDocument(document)),
        policyAction: (agree: boolean, clicked: boolean) => dispatch(policyAction(agree, clicked)),
        putPolicyAccept: (putLink: Link) => dispatch(putPolicyAccept(putLink))
    };
};

export default connect<ViewApplicationProps, ViewApplicationDispatchProps, void>(
    mapStateToProps,
    mapDispatchToProps
)(ViewApplication);



// WEBPACK FOOTER //
// ./src/scenes/candidate/index.tsx